import React from 'react';
import { Typography } from '@mui/material';

/**
 * Renders one section body of a legal text mirrored from the app.
 *
 * The privacy policy and terms are copied verbatim from the app's own screens
 * (mecfs: dsgvoDeclarationContent.ts / AGBScreen.tsx) so both texts stay
 * identical. Those sources store bodies as plain strings with "\n" line breaks,
 * "•" bullet markers and "**bold**" emphasis — this splits them back into
 * paragraphs and lists rather than restructuring the text, so a future version
 * bump stays a copy-paste.
 */

/** Renders "**bold**" spans inline; everything else stays plain text. */
const withEmphasis = (text: string): React.ReactNode =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, idx) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={idx}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={idx}>{part}</React.Fragment>
    )
  );

export const LegalSectionBody: React.FC<{ body: string }> = ({ body }) => {
  const blocks: Array<{ type: 'text' | 'bullets'; lines: string[] }> = [];

  body.split('\n').forEach((line) => {
    const last = blocks[blocks.length - 1];
    if (line.startsWith('•')) {
      if (last?.type === 'bullets') last.lines.push(line.slice(1).trim());
      else blocks.push({ type: 'bullets', lines: [line.slice(1).trim()] });
    } else {
      if (line.trim() === '') return;
      if (last?.type === 'text') last.lines.push(line);
      else blocks.push({ type: 'text', lines: [line] });
    }
  });

  return (
    <>
      {blocks.map((block, idx) =>
        block.type === 'bullets' ? (
          <ul key={idx}>
            {block.lines.map((item, i) => (
              <li key={i}>
                <Typography variant="body1" color="text.primary">
                  {withEmphasis(item)}
                </Typography>
              </li>
            ))}
          </ul>
        ) : (
          <Typography
            key={idx}
            variant="body1"
            paragraph
            color="text.primary"
            sx={{ whiteSpace: 'pre-line' }}
          >
            {withEmphasis(block.lines.join('\n'))}
          </Typography>
        )
      )}
    </>
  );
};
